import type {
    CreateSignOffEnvelopeRequest,
    DocumentSignatureStatus,
    ICreateSignOffCommentRequest,
    IDocumentSignature,
    ISignOffComment,
    ISignOffDocument,
    ISignOffEnvelope,
    ISignOffEnvelopeFilter,
    ISignOffFormBuilderRequest,
    IUpdateSignOffCommentParams,
    LinkSignOffEnvelopeConstructionPlanParams,
    SignOffEnvelopeExpandOpts,
    UpdateSignOffEnvelopeParams,
    UpdateSignOffEnvelopeInternalApproversParams,
    UpdateSignOffDocumentSignerParams,
} from '@condo/domain';
import { pick } from 'lodash-es';
import { getClient } from './api-client';

export const getSignOffEnvelopes = async (
    params?: ISignOffEnvelopeFilter,
    expand?: SignOffEnvelopeExpandOpts,
): Promise<{ signOffEnvelopes: ISignOffEnvelope[]; total: number }> =>
    getClient('basecamp')
        .get('/sign-off-envelopes', { params: { ...params, expand } })
        .then(response => response.data);

export const getSignOffEnvelope = async (signOffEnvelopeId: number, expand?: SignOffEnvelopeExpandOpts): Promise<ISignOffEnvelope> =>
    getClient('basecamp')
        .get(`/sign-off-envelopes/${signOffEnvelopeId}`, { params: { expand } })
        .then(response => response.data.signOffEnvelope);

export const createSignOffEnvelope = async (signOffEnvelope: CreateSignOffEnvelopeRequest): Promise<ISignOffEnvelope> =>
    getClient('basecamp')
        .post('/sign-off-envelopes', signOffEnvelope)
        .then(response => response.data.signOffEnvelope);

export const cancelSignOffEnvelope = async (signOffEnvelopeId: number): Promise<ISignOffEnvelope> =>
    getClient('basecamp')
        .delete(`/sign-off-envelopes/${signOffEnvelopeId}`)
        .then(response => response.data.signOffEnvelope);

export const deleteSignOffEnvelope = async (signOffEnvelopeId: number): Promise<void> =>
    getClient('basecamp')
        .delete(`/sign-off-envelopes/${signOffEnvelopeId}/delete`)
        .then(() => undefined);

export const updateSignOffEnvelope = async (updateParams: UpdateSignOffEnvelopeParams): Promise<ISignOffEnvelope> =>
    getClient('basecamp')
        .patch(`/sign-off-envelopes/${updateParams.signOffEnvelopeId}`, pick(updateParams, 'signOffDocuments'))
        .then(response => response.data.signOffEnvelope);
export const linkSignOffEnvelopeConstructionPlan = async ({
    signOffEnvelopeId,
    constructionPlanId,
}: LinkSignOffEnvelopeConstructionPlanParams): Promise<ISignOffEnvelope> =>
    getClient('basecamp')
        .patch(`/sign-off-envelopes/${signOffEnvelopeId}/construction-plan`, { constructionPlanId })
        .then(response => response.data.signOffEnvelope);

export const getSignOffTemplateToken = async (params: ISignOffFormBuilderRequest) =>
    getClient('basecamp')
        .post('/sign-off-document-form-builder', params)
        .then(response => response.data.token);

export const createSignOffComment = async (request: ICreateSignOffCommentRequest): Promise<ISignOffComment> =>
    getClient('basecamp')
        .post('/sign-off-comments', request)
        .then(response => response.data.signOffComment);

export const updateSignOffComment = async (commentId: number, request: Partial<IUpdateSignOffCommentParams>): Promise<ISignOffComment> =>
    getClient('basecamp')
        .put(`/sign-off-comments/${commentId}`, request)
        .then(response => response.data.signOffComment);

export const deleteSignOffComment = async (commentId: number): Promise<void> =>
    getClient('basecamp')
        .delete(`/sign-off-comments/${commentId}`)
        .then(() => {});

export const updateSignOffDocumentSignatures = async (params: {
    signOffDocumentId: number;
    signatures: Array<{
        isInternalSignature: boolean;
        contactId: number;
        userRole: string;
        userSigningOrder: number | null;
    }>;
    templateId?: number | null;
}): Promise<any> =>
    getClient('basecamp')
        .patch(`/sign-off-documents/${params.signOffDocumentId}/signatures`, pick(params, 'signatures', 'templateId'))
        .then(response => response.data.signOffDocument);

export const updateSignOffDocumentSigner = async (params: UpdateSignOffDocumentSignerParams): Promise<IDocumentSignature> =>
    getClient('basecamp')
        .patch(`/sign-off-document-signatures/${params.documentSignatureId}/signer`, pick(params, 'contactId'))
        .then(response => response.data.documentSignature);

export const updateSignOffEnvelopeApprovers = async (params: {
    signOffEnvelopeId: number;
    approvers: Array<{
        contactId: number;
    }>;
}): Promise<ISignOffEnvelope> =>
    getClient('basecamp')
        .patch(`/sign-off-envelopes/${params.signOffEnvelopeId}/approvers`, pick(params, 'approvers'))
        .then(response => response.data.signOffEnvelope);

export const updateSignOffEnvelopeInternalApprovers = async (params: UpdateSignOffEnvelopeInternalApproversParams): Promise<ISignOffEnvelope> =>
    getClient('basecamp')
        .patch(`/sign-off-envelopes/${params.signOffEnvelopeId}/internal-approvers`, pick(params, 'internalApproverContactIds'))
        .then(response => response.data.signOffEnvelope);

export const processSignOffEnvelopeEvent = async (params: { signOffEnvelopeId: number; event: string }): Promise<ISignOffEnvelope> =>
    getClient('basecamp')
        .post(`/sign-off-envelopes/${params.signOffEnvelopeId}/events`, pick(params, 'event'))
        .then(response => response.data.signOffEnvelope);

export const processSignOffDocumentEvent = async (params: { signOffDocumentId: number; event: string; rejectionReason?: string }): Promise<ISignOffDocument> =>
    getClient('basecamp')
        .post(`/sign-off-documents/${params.signOffDocumentId}/events`, pick(params, 'event', 'rejectionReason'))
        .then(response => response.data.signOffDocument);

export const processSignOffDocumentSignature = async (params: {
    signOffDocumentId: number;
    contactId: number;
    event: DocumentSignatureStatus;
}): Promise<ISignOffDocument> =>
    getClient('basecamp')
        .post(`/sign-off-documents/${params.signOffDocumentId}/signature`, pick(params, 'event', 'contactId'))
        .then(response => response.data.signOffDocument);
